import React, { useContext } from "react";
import classNames from "classnames";
import { Menu } from "@design-system/Menu/Menu.lib";
import { TransactionRuleManagementContext } from "@components/transactionRules/TransactionRuleManagementContext/TransactionRuleManagement.context";
import {
    RuleSource,
    TransactionRuleGroup,
} from "@common/types/transactionRule";
import { KICK_GROUP_ICON } from "@common/constants/rules";
import { useOverlays } from "contexts/OverlayContext";
import { useIdCopy } from "@hooks/useIdCopy";
import { Button } from "@design-system/Button/Button";
import { KickLogo } from "@design-system/KickLogo/KickLogo";
import { DragHandle6DotIcon, PlusIcon, TriangleIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import styles from "../RulesTable.module.scss";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

/**
 * GroupHeaderCell renders the group title and drag handle.
 */
interface GroupHeaderCellProps {
    readonly group: TransactionRuleGroup;
    readonly groupListeners?: React.HTMLAttributes<HTMLElement>;
    readonly groupAttributes?: React.HTMLAttributes<HTMLElement>;
    rowSpan?: number;
    readonly toggleCollapse?: () => void;
    readonly isCollapsed?: boolean;
    readonly priority?: number;
}

export const GroupHeaderCell: React.FC<GroupHeaderCellProps> = ({
    group,
    groupListeners,
    groupAttributes,
    rowSpan,
    toggleCollapse,
    isCollapsed,
    priority,
}) => {
    const { openOverlay } = useOverlays();
    const { updateRuleGroup, deleteRuleGroup } = useContext(
        TransactionRuleManagementContext,
    );
    const { getCopyProps } = useIdCopy({ label: "Rule Group" });

    const isKickGroup = group.icon === KICK_GROUP_ICON;

    return (
        <>
            <td
                className={classNames(
                    styles.td,
                    styles.groupHeader,
                    styles.priorityColumn,
                )}
                rowSpan={rowSpan ?? group.rules?.length ?? 1}
            >
                {priority}
            </td>
            <td
                className={classNames(styles.td, styles.groupHeader)}
                rowSpan={rowSpan ?? group.rules?.length ?? 1}
            >
                <HStack justify="between" align="start">
                    <HStack gap={8} align="start">
                        {isKickGroup ? (
                            <HStack gap={12} align="center">
                                <KickLogo variant="icon" size="xs" />
                                <span>Kick</span>
                            </HStack>
                        ) : (
                            <span {...getCopyProps(group.id)}>
                                {group.name}
                            </span>
                        )}

                        {toggleCollapse && (
                            <Button
                                variant="secondary"
                                size="sm"
                                iconOnly
                                aria-label={
                                    isCollapsed
                                        ? "Expand group"
                                        : "Collapse group"
                                }
                                className={classNames(styles.visibleOnHover, {
                                    [styles.collapsedCarot]: isCollapsed,
                                })}
                                onClick={() => toggleCollapse()}
                            >
                                <TriangleIcon className="icon-color-gray-500" />
                            </Button>
                        )}
                    </HStack>
                    <HStack gap={4}>
                        {!isKickGroup && (
                            <>
                                <Menu.More
                                    className={classNames(
                                        styles.dragButton,
                                        styles.managementDropdown,
                                    )}
                                >
                                    <Menu.Item
                                        onClick={() => updateRuleGroup(group)}
                                    >
                                        Edit
                                    </Menu.Item>
                                    <Menu.Item
                                        onClick={() => deleteRuleGroup(group)}
                                        destructive
                                    >
                                        Delete
                                    </Menu.Item>
                                </Menu.More>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    iconOnly
                                    {...groupListeners}
                                    {...groupAttributes}
                                    tabIndex={-1}
                                    aria-label="Drag group"
                                    className={styles.dragButton}
                                >
                                    <DragHandle6DotIcon />
                                </Button>
                            </>
                        )}
                        <Tooltip content="Add rule">
                            <Button
                                variant="secondary"
                                size="sm"
                                iconOnly
                                aria-label="Add rule"
                                onClick={() => {
                                    openOverlay("createRule", {
                                        source: RuleSource.SETTINGS,
                                        definition: {
                                            groupId: group.id,
                                            conditions: [],
                                        },
                                        groupType: group.type,
                                    });
                                }}
                            >
                                <PlusIcon />
                            </Button>
                        </Tooltip>
                    </HStack>
                </HStack>
            </td>
        </>
    );
};
